import { useEffect, useState } from 'react'
import { withDecisionDate } from '../../utils/appliedDate'
import { useNavigate } from 'react-router-dom'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  Alert,
  Button,
  Stack,
  CircularProgress,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  RadioButtonUnchecked as UncheckedIcon,
  Task as TaskIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  Notifications as DigestIcon,
  HelpOutline as DecisionIcon,
} from '@mui/icons-material'
import PageHelpButton from '../../components/PageHelpButton'
import { usePortalData } from '../../components/portal/PortalDataProvider'
import { apiCall, jobOpportunitiesApi } from '../../services/api'
import {
  awaitingDecisionJobsAtom,
  jobOpportunitiesAtom,
  APPLY_DECISIONS,
  APPLY_DECISION_COLORS,
} from '../../state/jobOpportunities'
import { colors } from '../../theme'
import { workflowStageDefinitionsAtom } from '../../state/clients'
import { portalStageLabel } from '../../utils/portalStage'

export default function PortalDashboard() {
  const navigate = useNavigate()
  // The provider has already made this call for the whole portal session, so
  // null here is a failed fetch rather than one still in flight.
  const { dashboard: data, workflowStage, visibility } = usePortalData()
  const stageDefinitions = useAtomValue(workflowStageDefinitionsAtom)
  const stageLabel = portalStageLabel(workflowStage, stageDefinitions)
  const [digestItems, setDigestItems] = useState<{ contactId: string; contactName: string; company: string; message: string }[]>([])
  const [awaitingDecisionJobs] = useAtom(awaitingDecisionJobsAtom)
  const setJobs = useSetAtom(jobOpportunitiesAtom)
  // Per-job saving state so each card's buttons disable independently.
  const [savingJobId, setSavingJobId] = useState<string | null>(null)

  const handleApplyDecision = async (jobId: string, value: string) => {
    setSavingJobId(jobId)
    try {
      const updated = await jobOpportunitiesApi.update(withDecisionDate({ id: jobId, applyDecision: value }))
      // Replace by id so the job drops off the awaiting-decision list.
      setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)))
    } catch (err) {
      console.error('Failed to record application decision:', err)
    } finally {
      setSavingJobId(null)
    }
  }

  useEffect(() => {
    const fetchDigest = async () => {
      try {
        type DigestItem = { contactId: string; contactName: string; company: string; message: string }
        type DigestMetadata = { items?: DigestItem[] }
        const notifications = await apiCall<
          { id: string; type: string; message: string; entityType: string; createdAt: string; metadata?: DigestMetadata }[]
        >('list-notifications?type=digest&limit=1')
        if (Array.isArray(notifications) && notifications.length > 0) {
          const latest = notifications[0]
          // Prefer structured metadata (includes contactId for deep-linking).
          // Fall back to split-by-newline for notifications predating the
          // metadata field so in-flight digests still render.
          if (latest.metadata?.items?.length) {
            setDigestItems(latest.metadata.items)
          } else {
            setDigestItems(
              latest.message.split('\n').filter(Boolean).map((msg) => ({
                contactId: '',
                contactName: '',
                company: '',
                message: msg,
              }))
            )
          }
        }
      } catch {
        // Digest not available — that's fine
      }
    }

    fetchDigest()
  }, [])

  if (!data) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        Failed to load dashboard data
      </Alert>
    )
  }

  const formItems = [
    {
      name: 'Resume',
      completed: data.formCompletion.intake.completed,
      path: '/portal/profile?tab=resume',
      description: 'Upload your current resume (PDF or text file)',
    },
    {
      name: 'Dream Job Profile',
      completed: data.formCompletion.dreamJob.completed,
      path: '/portal/profile?tab=dreamjob',
      description: 'Your ideal role, salary, and preferences',
    },
    // A stage that hides the tab would leave this item opening a different one.
    ...(visibility.isEntryVisible('clifton-strengths')
      ? [
          {
            name: 'Clifton Strengths',
            completed: data.formCompletion.cliftonStrengths.completed,
            path: '/portal/profile?tab=clifton-strengths',
            description: 'Your top 5 CliftonStrengths themes',
          },
        ]
      : []),
  ]

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="h4" component="h1">
          Welcome back, {data.client.name}!
        </Typography>
        <PageHelpButton articleId="page-portal-dashboard" />
      </Stack>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Complete your profile so your strategist can get to work.
      </Typography>

      <Grid container spacing={3}>
        {/* Progress Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TrendingUpIcon sx={{ color: colors.primary, mr: 1 }} />
                <Typography variant="h6">Your Progress</Typography>
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography variant="h3" sx={{ color: colors.primary, fontWeight: 700 }}>
                  {data.progress.percentComplete}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {data.progress.formsCompleted} of {data.progress.totalForms} forms completed
                </Typography>
              </Box>
              {stageLabel && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, overflowWrap: 'anywhere' }}>
                  Current stage:{' '}
                  <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                    {stageLabel}
                  </Box>
                </Typography>
              )}
              <LinearProgress
                variant="determinate"
                value={data.progress.percentComplete}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(0,0,0,0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: colors.primary,
                    borderRadius: 4,
                  },
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Tasks Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TaskIcon sx={{ color: colors.primary, mr: 1 }} />
                <Typography variant="h6">Pending Tasks</Typography>
              </Box>
              <Typography variant="h3" sx={{ color: colors.primary, fontWeight: 700, mb: 1 }}>
                {data.pendingTaskCount}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                tasks waiting for you
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate('/portal/profile?tab=tasks')}
                disabled={data.pendingTaskCount === 0}
              >
                View Tasks
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Resume Status Card */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DescriptionIcon sx={{ color: colors.primary, mr: 1 }} />
                <Typography variant="h6">Resume Status</Typography>
              </Box>
              {data.resume ? (
                <>
                  <Chip
                    label={data.resume.status.replace('_', ' ')}
                    color={data.resume.status === 'complete' ? 'success' : 'warning'}
                    sx={{ mb: 2 }}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Version {data.resume.version}
                  </Typography>
                  <Button variant="outlined" size="small" onClick={() => navigate('/portal/profile?tab=my-resume')}>
                    View Resume
                  </Button>
                </>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Your strategist writes your resume from your profile and conversations.
                  </Typography>
                  <Chip label="Not Started" color="default" />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Today's Digest Card */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <DigestIcon sx={{ color: colors.primary, mr: 1 }} />
                <Typography variant="h6">Today's Digest</Typography>
              </Box>
              {digestItems.length > 0 ? (
                <List dense>
                  {digestItems.map((item, idx) => {
                    const target = item.contactId
                      ? `/portal/networking?contactId=${encodeURIComponent(item.contactId)}`
                      : '/portal/networking'
                    return (
                      <ListItem
                        key={idx}
                        sx={{
                          borderRadius: 2,
                          mb: 0.5,
                          cursor: 'pointer',
                          '&:hover': { backgroundColor: 'action.hover' },
                        }}
                        onClick={() => navigate(target)}
                      >
                        <ListItemText
                          primary={item.message}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                        <Button
                          variant="text"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(target)
                          }}
                        >
                          Follow Up
                        </Button>
                      </ListItem>
                    )
                  })}
                </List>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  You're all caught up! No contacts need attention right now.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Awaiting Your Decision Card — staff-sourced applications needing a choice */}
        {awaitingDecisionJobs.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DecisionIcon sx={{ color: colors.primary, mr: 1 }} />
                  <Typography variant="h6">Awaiting your decision</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  We added these applications for you — let us know how you'd like to proceed.
                </Typography>
                <Stack spacing={2}>
                  {awaitingDecisionJobs.map((job) => (
                    <Box
                      key={job.id}
                      sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        justifyContent: 'space-between',
                        gap: 1.5,
                        p: 2,
                        borderRadius: 2,
                        border: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {job.company} — {job.jobTitle}
                      </Typography>
                      <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                        {APPLY_DECISIONS.map((decision) => (
                          <Button
                            key={decision.value}
                            size="small"
                            variant="outlined"
                            disabled={savingJobId === job.id}
                            startIcon={
                              savingJobId === job.id ? <CircularProgress size={14} /> : undefined
                            }
                            onClick={() => handleApplyDecision(job.id, decision.value)}
                            sx={{
                              borderColor: APPLY_DECISION_COLORS[decision.value],
                              color: APPLY_DECISION_COLORS[decision.value],
                              '&:hover': {
                                borderColor: APPLY_DECISION_COLORS[decision.value],
                                backgroundColor: 'action.hover',
                              },
                            }}
                          >
                            {decision.label}
                          </Button>
                        ))}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Profile Completion Card */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <PersonIcon sx={{ color: colors.primary, mr: 1 }} />
                <Typography variant="h6">Complete Your Profile</Typography>
              </Box>
              <List>
                {formItems.map((item) => (
                  <ListItem
                    key={item.name}
                    onClick={() => navigate(item.path)}
                    sx={{
                      borderRadius: 2,
                      mb: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon>
                      {item.completed ? (
                        <CheckCircleIcon sx={{ color: 'success.main' }} />
                      ) : (
                        <UncheckedIcon sx={{ color: 'text.disabled' }} />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {item.name}
                          {item.completed && (
                            <Chip label="Complete" size="small" color="success" />
                          )}
                        </Box>
                      }
                      secondary={item.description}
                    />
                    <Button variant="text" size="small">
                      {item.completed ? 'Edit' : 'Start'}
                    </Button>
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  )
}
